import React, { useState, useEffect } from "react";
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField } from "@mui/material";

const PRESET_LABELS = [
  "Today",
  "Yesterday",
  "This Week",
  "Last Week",
  "This Month",
  "Last Month",
  "This Year",
  "Last Year",
  "Custom Range",
];

function toISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getPresetRange(preset) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let start, end;
  switch (preset) {
    case "Today":
      start = end = today;
      break;
    case "Yesterday": {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      start = end = yesterday;
      break;
    }
    case "This Week": {
      const day = today.getDay();
      const sundayOffset = day === 0 ? 0 : -day;
      start = new Date(today);
      start.setDate(today.getDate() + sundayOffset);
      end = new Date(today);
      break;
    }
    case "Last Week": {
      const day = today.getDay();
      const lastSunday = new Date(today);
      lastSunday.setDate(today.getDate() - (day === 0 ? 7 : day));
      start = new Date(lastSunday);
      end = new Date(lastSunday);
      end.setDate(end.getDate() + 6);
      break;
    }
    case "This Month":
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today);
      break;
    case "Last Month":
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
      break;
    case "This Year":
      start = new Date(today.getFullYear(), 0, 1);
      end = new Date(today);
      break;
    case "Last Year":
      start = new Date(today.getFullYear() - 1, 0, 1);
      end = new Date(today.getFullYear() - 1, 11, 31);
      break;
    default:
      return null;
  }
  return { start_date: toISO(start), end_date: toISO(end) };
}

/**
 * Shared date range filter with presets.
 * @param {Object} props
 * @param {Object} props.value - { start_date?, end_date? } (YYYY-MM-DD)
 * @param {Function} props.onChange - (newValue) => void
 * @param {Function} [props.onApply] - called when Apply is clicked (optional; if not provided, preset clicks apply immediately)
 * @param {boolean} [props.showApplyForCustom] - if true, Custom Range shows an Apply button
 * @param {string} [props.startLabel] - e.g. "Start Date" or "Date from"
 * @param {string} [props.endLabel] - e.g. "End Date" or "Date to"
 * @param {Object} [props.dateKeys] - { start: 'start_date', end: 'end_date' } for different key names (e.g. date_from/date_to)
 */
export default function DateRangeFilter({
  value = {},
  onChange,
  onApply,
  showApplyForCustom = true,
  startLabel = "Start Date",
  endLabel = "End Date",
  dateKeys = { start: "start_date", end: "end_date" },
}) {
  const sk = dateKeys.start;
  const ek = dateKeys.end;
  const startVal = value[sk] ?? "";
  const endVal = value[ek] ?? "";

  const [customStart, setCustomStart] = useState(startVal);
  const [customEnd, setCustomEnd] = useState(endVal);
  const [preset, setPreset] = useState("This Month");

  useEffect(() => {
    setCustomStart(startVal);
    setCustomEnd(endVal);
  }, [startVal, endVal]);

  const applyPreset = (presetName) => {
    if (presetName === "Custom Range") {
      setPreset("Custom Range");
      return;
    }
    const range = getPresetRange(presetName);
    if (range) {
      setPreset(presetName);
      onChange({ ...value, [sk]: range.start_date, [ek]: range.end_date });
      onApply?.();
    }
  };

  const handleApplyCustom = () => {
    setPreset("Custom Range");
    onChange({ ...value, [sk]: customStart || undefined, [ek]: customEnd || undefined });
    onApply?.();
  };

  return (
    <Box>
      <FormControl size="small" sx={{ minWidth: 200, mb: preset === "Custom Range" ? 2 : 0 }}>
        <InputLabel id="date-range-preset-label">Date range</InputLabel>
        <Select
          labelId="date-range-preset-label"
          value={preset}
          label="Date range"
          onChange={(e) => applyPreset(e.target.value)}
        >
          {PRESET_LABELS.map((label) => (
            <MenuItem key={label} value={label}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {preset === "Custom Range" && (
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <TextField
            label={startLabel}
            type="date"
            size="small"
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          <TextField
            label={endLabel}
            type="date"
            size="small"
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          {showApplyForCustom && (
            <Button variant="contained" onClick={handleApplyCustom}>
              Apply
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
}

export { getPresetRange, PRESET_LABELS };
